/**
 * Module: web/src/stores/auth.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.1.0
 * Date: 2026-07-16
 *
 * v1.1.0 (assisted-first landing -- Robert, 2026-07-16): landing precedence
 * gains assisted_entry_review -> /assisted-entry between the manager tier
 * and data_entry. A pure dispatcher (Heather, Bonnie) now lands on the
 * Assisted-Entry Review queue, not the manual entry form -- the queue is the
 * primary entry path. Managers keep /reports (reporting_view wins first);
 * data_entry-only users keep /dispatch; drivers keep /my-entries.
 *
 * Auth store -- the real replacement for firstline-insight's 22-line stub
 * (doc 04 sect 1.1 / sect 10). Holds the session identity IN MEMORY ONLY
 * (never localStorage -- caps re-hydrate via GET /auth/whoami on every boot).
 *
 * Supports both session kinds (doc 04 sect 6.2 + 6.5):
 *   full -- { user, roles, effective_caps, must_change_password }
 *   pin  -- { session_kind:'pin', person, pin_caps } (limited quick-access lane)
 *
 * hasCap() for a PIN session is satisfied ONLY by the PIN scope -- a PIN never
 *  elevates to the person's full-account capabilities (doc 04 sect 6.5).
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiGet, apiPost } from '@/composables/useApi';
import {
  isPinSession,
  type AuthUser,
  type FullSession,
  type PersonRef,
  type PinSession,
  type WhoamiResponse,
} from '@/types/api';

export type SessionKind = 'full' | 'pin' | null;

export const useAuthStore = defineStore('auth', () => {
  // ---- state (in-memory only) ---------------------------------------------
  const user = ref<AuthUser | null>(null);
  const effectiveCaps = ref<string[]>([]);
  const sessionKind = ref<SessionKind>(null);
  const person = ref<PersonRef | null>(null); // PIN sessions only
  const pinCaps = ref<string[]>([]);
  const mustChange = ref(false);
  /** true once the first whoami round-trip has settled (authed or not). */
  const ready = ref(false);

  // ---- derived -------------------------------------------------------------
  const roles = computed<string[]>(() => user.value?.roles ?? []);
  const isAuthenticated = computed(
    () => (sessionKind.value === 'full' && user.value !== null)
      || (sessionKind.value === 'pin' && person.value !== null),
  );
  const displayName = computed(() =>
    sessionKind.value === 'pin' ? (person.value?.name ?? 'Quick access') : (user.value?.name ?? ''),
  );

  /**
   * Workspace landing precedence (doc 04 sect 9.5; v1.1.0 assisted-first):
   *   PIN session                          -> /quick (dedicated limited shell)
   *   must_change_password                 -> /change-password (forced wall)
   *   reporting_view OR user_admin         -> /reports     (Manager Workspace)
   *   assisted_entry_review                -> /assisted-entry (Dispatcher
   *                                           Workspace -- the queue IS the
   *                                           primary entry path)
   *   data_entry                           -> /dispatch    (manual entry, the
   *                                           exception path)
   *   else                                 -> /my-entries  (Driver Workspace, read-only)
   */
  const landingPath = computed<string>(() => {
    if (sessionKind.value === 'pin') return '/quick';
    if (!user.value) return '/login';
    if (mustChange.value) return '/change-password';
    if (hasCap('reporting_view') || hasCap('user_admin')) return '/reports';
    if (hasCap('assisted_entry_review')) return '/assisted-entry';
    if (hasCap('data_entry')) return '/dispatch';
    return '/my-entries';
  });

  // ---- capability check ----------------------------------------------------
  function hasCap(cap: string): boolean {
    if (sessionKind.value === 'pin') {
      return pinCaps.value.includes(cap); // PIN scope ONLY -- never the full-account caps
    }
    return effectiveCaps.value.includes(cap);
  }

  // ---- session application -------------------------------------------------
  function applyFullSession(resp: FullSession) {
    user.value = resp.user;
    effectiveCaps.value = resp.effective_caps ?? [];
    mustChange.value = resp.must_change_password === true;
    sessionKind.value = 'full';
    person.value = null;
    pinCaps.value = [];
  }

  function applyPinSession(resp: PinSession) {
    person.value = resp.person;
    pinCaps.value = resp.pin_caps ?? [];
    sessionKind.value = 'pin';
    user.value = null;
    effectiveCaps.value = [];
    mustChange.value = false;
  }

  function clearSession() {
    user.value = null;
    effectiveCaps.value = [];
    sessionKind.value = null;
    person.value = null;
    pinCaps.value = [];
    mustChange.value = false;
  }

  // ---- actions --------------------------------------------------------------
  /** Hydrate from the cookie session (GET /auth/whoami). 401 = not logged in. */
  async function hydrate(): Promise<void> {
    try {
      const resp = await apiGet<WhoamiResponse>('/auth/whoami');
      if (isPinSession(resp)) applyPinSession(resp);
      else applyFullSession(resp);
    } catch {
      clearSession(); // 401 (or backend down) -> unauthenticated
    } finally {
      ready.value = true;
    }
  }

  /** Full login. Throws ApiError (401 invalid / 423 locked) for the view to
   *  render. ``remember`` = "Trust this device" (decision #167): the server
   *  issues a 30-day trusted-device session instead of the 12h/2h default. */
  async function login(email: string, password: string, remember = false): Promise<FullSession> {
    const resp = await apiPost<FullSession>('/auth/login', { email, password, remember });
    applyFullSession(resp);
    ready.value = true;
    return resp;
  }

  /** Quick-PIN partial login (doc 04 sect 6.5). Throws ApiError 401/423. */
  async function pinLogin(personRef: string, pin: string): Promise<PinSession> {
    const resp = await apiPost<PinSession>('/auth/pin-login', { person_ref: personRef, pin });
    applyPinSession(resp);
    ready.value = true;
    return resp;
  }

  /** Change password (forced or voluntary). 204 on success; 400 {code:'policy'} / 401 throw. */
  async function changePassword(currentPw: string, newPw: string): Promise<void> {
    await apiPost<void>('/auth/change-password', { current_pw: currentPw, new_pw: newPw });
    mustChange.value = false;
    await hydrate(); // pick up any server-side session/cap changes post-change
  }

  /** Logout -- best-effort server revoke, then always clear local state. */
  async function logout(): Promise<void> {
    try {
      await apiPost<void>('/auth/logout');
    } catch {
      /* session may already be dead -- clear regardless */
    }
    clearSession();
  }

  return {
    // state
    user, effectiveCaps, sessionKind, person, pinCaps, mustChange, ready,
    // derived
    roles, isAuthenticated, displayName, landingPath,
    // actions
    hasCap, hydrate, login, pinLogin, changePassword, logout, clearSession,
  };
});
